import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { performanceCycleApi } from '../lib/api';

const PerformanceCycleContext = createContext(null);

export function PerformanceCycleProvider({ children }) {
  const [performanceCycle, setPerformanceCycle] = useState(null);
  const [loading, setLoading] = useState(false);
  const currentYear = new Date().getFullYear();

  const loadPerformanceCycle = useCallback(async () => {
    // Only load if there's a token (user is logged in)
    const token = localStorage.getItem('token');
    if (!token) {
      // Set default values when not logged in
      setPerformanceCycle({
        year: currentYear,
        goal_setting_start: `${currentYear}-01-01`,
        mid_year_start: `${currentYear}-07-01`,
        year_end_start: `${currentYear + 1}-01-01`,
        calibration_start: `${currentYear + 1}-02-01`,
        stages: {
          goal_setting: { is_active: true },
          mid_year: { is_active: false },
          year_end: { is_active: false },
          calibration: { is_active: false },
        }
      });
      return;
    }

    setLoading(true);
    try {
      const response = await performanceCycleApi.get(currentYear);
      setPerformanceCycle(response.data);
    } catch (error) {
      console.error('Failed to load performance cycle:', error);
      // Set default values if API fails
      setPerformanceCycle({
        year: currentYear,
        goal_setting_start: `${currentYear}-01-01`,
        mid_year_start: `${currentYear}-07-01`,
        year_end_start: `${currentYear + 1}-01-01`,
        calibration_start: `${currentYear + 1}-02-01`,
        stages: {
          goal_setting: { is_active: true },
          mid_year: { is_active: false },
          year_end: { is_active: false },
          calibration: { is_active: false },
        }
      });
    } finally {
      setLoading(false);
    }
  }, [currentYear]);

  // Load on mount and when token changes
  useEffect(() => {
    loadPerformanceCycle();
    
    // Listen for storage changes (login/logout)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        loadPerformanceCycle();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadPerformanceCycle]);

  const refreshCycle = () => {
    loadPerformanceCycle();
  };

  const isStageActive = (stageName) => {
    if (!performanceCycle?.stages) return false;
    return performanceCycle.stages[stageName]?.is_active || false;
  };

  const isStageEnabled = (stageName) => {
    if (!performanceCycle) return true; // Default to enabled
    const enabledKey = `${stageName}_enabled`;
    return performanceCycle[enabledKey] !== false; // Default to true if not set
  };

  const getStageStartDate = (stageName) => {
    if (!performanceCycle) return null;
    const dateKey = `${stageName}_start`;
    return performanceCycle[dateKey] || null;
  };

  const formatStageDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return '';
    }
  };

  return (
    <PerformanceCycleContext.Provider value={{
      performanceCycle,
      loading,
      refreshCycle,
      isStageActive,
      isStageEnabled,
      getStageStartDate,
      formatStageDate,
      currentYear,
    }}>
      {children}
    </PerformanceCycleContext.Provider>
  );
}

export function usePerformanceCycle() {
  const context = useContext(PerformanceCycleContext);
  if (!context) {
    throw new Error('usePerformanceCycle must be used within a PerformanceCycleProvider');
  }
  return context;
}
